import { FC, useCallback, useMemo, useState } from 'react';

import { useContentManagement } from '../../api/portal/useContentManagement';
import { EFieldState } from '../../constants/enums';
import { UpdateTagConfirmationModal } from '../../views/Content/ContentForm/UpdateTagConfirmationModal';
import type { InputSelectDropdownOption } from '../InputSelectDropdown/InputSelectDropdown';
import { InputSelectDropdownWithActions } from '../InputSelectDropdownWithActions/InputSelectDropdownWithActions';

enum ConfirmModalType {
  Edit = 'edit',
  Delete = 'delete'
}

type ConfirmModalState = {
  type: ConfirmModalType;
  option: InputSelectDropdownOption;
} | null;

export type PortalTag = {
  id: string;
  name: string;
};

type TagSelectorProps = {
  value?: string;
  onChange?: (value: string | undefined) => void;
  error?: string;
};

export const TagSelector: FC<TagSelectorProps> = ({
  value,
  onChange,
  error
}) => {
  const { getPortalTags, createTag, updateTag, deleteTag } = useContentManagement();

  const availableTags = useMemo<PortalTag[]>(
    () => getPortalTags?.data ?? [],
    [getPortalTags?.data]
  );
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>(null);

  const options = useMemo<InputSelectDropdownOption[]>(
    () => availableTags.map(tag => ({ value: tag.id, label: tag.name })),
    [availableTags]
  );

  const selectedOption = useMemo(
    () => options.find(opt => opt.label === value),
    [options, value]
  );

  const handleCreate = useCallback(
    async (newValue: string) => {
      const newTag = newValue.trim();
      if (availableTags.some(tag => tag.name === newTag)) return;

      try {
        await createTag.mutateAsync(newTag);
        onChange?.(newTag);
      } catch (error) {
        console.error('Failed to create tag:', error);
      }
    },
    [availableTags, createTag, onChange]
  );

  const handleEditRequest = useCallback(
    (option: InputSelectDropdownOption) => {
      setConfirmModal({ type: ConfirmModalType.Edit, option });
    },
    []
  );

  const handleDeleteRequest = useCallback(
    (option: InputSelectDropdownOption) => {
      setTimeout(() => {
        setConfirmModal({ type: ConfirmModalType.Delete, option });
      }, 0);
    },
    []
  );

  const handleConfirm = useCallback(async () => {
    if (!confirmModal) return;

    const tagId = String(confirmModal.option.value);

    try {
      if (confirmModal.type === ConfirmModalType.Edit) {
        const updatedName = confirmModal.option.label.trim();
        await updateTag.mutateAsync({ tagId, name: updatedName });
        onChange?.(updatedName);
      } else {
        await deleteTag.mutateAsync(tagId);
        if (value === confirmModal.option.label) {
          onChange?.(undefined);
        }
      }
    } catch (error) {
      console.error('Failed to update/delete tag:', error);
    }

    setConfirmModal(null);
  }, [confirmModal, value, onChange, updateTag, deleteTag]);

  const handleOptionChange = useCallback(
    (selectedValue: string) => {
      const selectedTag = availableTags.find(tag => tag.id === selectedValue);
      onChange?.(selectedTag?.name);
    },
    [availableTags, onChange]
  );

  return (
    <>
      <InputSelectDropdownWithActions
        label="Tag"
        placeholder="Select tag"
        searchPlaceholder="Search"
        noSearchResultsText="Select or create a tag"
        options={options}
        defaultSelected={selectedOption}
        onOptionChange={handleOptionChange}
        onCreate={handleCreate}
        onEdit={handleEditRequest}
        onDelete={handleDeleteRequest}
        state={error ? EFieldState.ERROR : EFieldState.DEFAULT}
      />
      {confirmModal && (
        <UpdateTagConfirmationModal
          isOpen={!!confirmModal}
          onCancel={() => setConfirmModal(null)}
          onConfirm={handleConfirm}
          isDelete={confirmModal.type === ConfirmModalType.Delete}
        />
      )}
    </>
  );
};
